import { useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'

import debbieBenningPhoto from '@/assets/quotes/debbie-benning.jpeg'
import {
  type ApplicationSummaryData,
  ApplicationSummarySection,
} from '@/components/quotes/ApplicationSummarySection'
import {
  type ClaimsCapabilityFilterKey,
  COVERAGE_FILTER_OPTIONS,
  FiltersSection,
  type QuoteSortBy,
} from '@/components/quotes/FiltersSection'
import { PlaceholderSection } from '@/components/quotes/PlaceholderSection'
import { QuoteCardsCarousel, type QuoteCarouselHandle } from '@/components/quotes/QuoteCardsCarousel'
import { CoiSpeed, QuoteCardStage } from '@/components/quotes/quoteCardTypes'
import { QuoteCartCheckout } from '@/components/quotes/QuoteCartCheckout'
import { QuoteCartProvider, useQuoteCart } from '@/components/quotes/QuoteCartContext'
import { filterAndSortQuotes } from '@/components/quotes/quoteFilters'
import { MyDocumentsSummary } from '@/components/quotes/summary/MyDocumentsSummary'
import { TopQuotesHeader } from '@/components/quotes/TopQuotesHeader'
import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { CoverageType } from '@/models/insuranceCoverage'
import { ProductFlowFooter } from '@/routes/Public/marketing/components/ProductFlowFooter'
import { ProductFlowNav } from '@/routes/Public/marketing/components/ProductFlowNav'
import { Cap } from '../../primitives'
import { PrototypeFrame } from '../../PrototypeFrame'
import { QUOTES } from '../QuoteCard/quoteCardData'

// Mocked — no real policy record backs this prototype.
const POLICY_EXPIRATION_DATE = '2026-08-15'

// What the mock customer asked to be covered for — "Best" ranks carriers that priced all of it first.
const REQUESTED_COVERAGES = new Set<string>([CoverageType.AUTO_LIABILITY, CoverageType.MOTOR_TRUCK_CARGO])

// Fixture org id — the documents fetch behind it is stubbed for captures.
const PROTOTYPE_ORG_ID = '00000000-0000-0000-0000-000000000001'

const SUMMARY_DATA: ApplicationSummaryData = {
  driverCount: 11,
  truckCount: 9,
  trailerCount: 4,
  telematicsConnectedTo: 'Samsara',
  claimsMissingData: 'Loss runs pending',
  content: { 'my-documents': <MyDocumentsSummary orgId={PROTOTYPE_ORG_ID} /> },
}

// No backend behind the prototype — a beat of latency so the checkout button's loading state is real.
async function submitCart() {
  await new Promise((resolve) => setTimeout(resolve, 600))
}

const MATCHED_BROKER = {
  name: 'Debbie Benning',
  phone: '(404) 555-0182',
  email: 'debbie@cai.ai',
  avatarUrl: debbieBenningPhoto,
}

const BRIEF = (
  <div className="border-ink-700 grid grid-cols-1 gap-6 border-t pt-6 sm:grid-cols-2 lg:grid-cols-4">
    <div>
      <Cap onDark>Goal</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Create a quote page that displays offers in a way that enables such seamless comparison shopping,
        customers want to upload quotes from other brokers. Also one that works on mobile.
      </p>
    </div>

    <div>
      <Cap onDark>Project: Sub-5 self-serve</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Enable brokers to focus their attention and expertise on quotes (not collecting information and
        filling applications), and helping bigger customers with more complex insurance decisions.
      </p>
    </div>

    <div>
      <Cap onDark>Feedback</Cap>
      <ul className="text-ink-200 mt-2 flex flex-col gap-1 text-xs leading-relaxed">
        <li>
          Slack channel:{' '}
          <a
            href="https://cailabsinc.slack.com/archives/C0BLBNGCZPF"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-300 hover:text-brand-200 underline underline-offset-2"
          >
            #test-quote-page-v1
          </a>
        </li>
        <li>
          <a
            href="https://www.figma.com/design/s5rPNMOjPfBAIbivX1nrpR/Quotes-Review?node-id=0-1&p=f&t=cTQVH75vhxJVY3KS-0"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-300 hover:text-brand-200 underline underline-offset-2"
          >
            Review Figma (screenshots)
          </a>
        </li>
      </ul>
    </div>

    <div>
      <Cap onDark>Next</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Finish out prototype around the application area, where customers can make updates and &quot;request
        re-quote,&quot; including My Documents and Additional Info modals.
      </p>
    </div>
  </div>
)

/**
 * Prototype for a standalone Quotes page — a full page (own top nav state +
 * product flow footer), not just a component, so it can be reviewed in
 * context rather than on the plain design-system canvas.
 */
export function QuotesPagePrototype() {
  return (
    <QuoteCartProvider>
      <QuotesPage />
    </QuoteCartProvider>
  )
}

function QuotesPage() {
  const [sortBy, setSortBy] = useState<QuoteSortBy>('best')
  const [selectedRatings, setSelectedRatings] = useState<Set<string>>(new Set())
  const [selectedCoiSpeeds, setSelectedCoiSpeeds] = useState<Set<CoiSpeed>>(new Set())
  const [selectedCoverages, setSelectedCoverages] = useState<Set<string>>(new Set())
  const [financingAvailable, setFinancingAvailable] = useState(false)
  const [selectedClaimsCapabilities, setSelectedClaimsCapabilities] = useState<
    Set<ClaimsCapabilityFilterKey>
  >(new Set())
  const [isUpdatingQuotes, setIsUpdatingQuotes] = useState(false)
  const cart = useQuoteCart()
  const isFirstRender = useRef(true)
  const carouselRef = useRef<QuoteCarouselHandle>(null)

  function toggleRating(rating: string) {
    setSelectedRatings((current) => {
      const next = new Set(current)
      if (next.has(rating)) next.delete(rating)
      else next.add(rating)
      return next
    })
  }

  function toggleCoiSpeed(speed: CoiSpeed) {
    setSelectedCoiSpeeds((current) => {
      const next = new Set(current)
      if (next.has(speed)) next.delete(speed)
      else next.add(speed)
      return next
    })
  }

  function toggleCoverage(coverage: string) {
    setSelectedCoverages((current) => {
      const next = new Set(current)
      if (next.has(coverage)) next.delete(coverage)
      else next.add(coverage)
      return next
    })
  }

  function toggleClaimsCapability(key: ClaimsCapabilityFilterKey) {
    setSelectedClaimsCapabilities((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function clearAllFilters() {
    setSelectedRatings(new Set())
    setSelectedCoiSpeeds(new Set())
    setSelectedCoverages(new Set())
    setFinancingAvailable(false)
    setSelectedClaimsCapabilities(new Set())
  }

  // No real backend in this prototype — sorting/filtering resolves instantly, so a
  // brief simulated delay gives the loading bar something to show. Skips the first
  // render so the bar doesn't flash on initial page load.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    setIsUpdatingQuotes(true)
    const timeout = setTimeout(() => setIsUpdatingQuotes(false), 600)
    return () => clearTimeout(timeout)
  }, [
    sortBy,
    selectedRatings,
    selectedCoiSpeeds,
    selectedCoverages,
    financingAvailable,
    selectedClaimsCapabilities,
  ])

  const visibleQuotes = useMemo(() => {
    return filterAndSortQuotes(QUOTES, {
      sortBy,
      selectedRatings,
      selectedCoiSpeeds,
      selectedCoverages,
      financingAvailable,
      selectedClaimsCapabilities,
      requestedCoverages: REQUESTED_COVERAGES,
    })
  }, [
    sortBy,
    selectedRatings,
    selectedCoiSpeeds,
    selectedCoverages,
    financingAvailable,
    selectedClaimsCapabilities,
  ])

  // Changes when the sort or any filter changes — resets the carousel to the first card.
  const orderKey = useMemo(
    () =>
      JSON.stringify([
        sortBy,
        [...selectedRatings].sort(),
        [...selectedCoiSpeeds].sort(),
        [...selectedCoverages].sort(),
        financingAvailable,
        [...selectedClaimsCapabilities].sort(),
      ]),
    [
      sortBy,
      selectedRatings,
      selectedCoiSpeeds,
      selectedCoverages,
      financingAvailable,
      selectedClaimsCapabilities,
    ]
  )

  // Coverages the customer has filtered to but that no current quote actually has —
  // prompts a "request quotes" action instead of silently showing an empty result.
  const missingCoverages = useMemo(
    () =>
      COVERAGE_FILTER_OPTIONS.filter(
        (option) =>
          selectedCoverages.has(option.value) &&
          !QUOTES.some(
            (quote) =>
              quote.stage === QuoteCardStage.PRICED &&
              quote.coverages.some((coverage) => coverage.type === option.value)
          )
      ),
    [selectedCoverages]
  )

  return (
    <PrototypeFrame
      title="Quotes page"
      owner="Carrie Phillips"
      status="Prototype, 0 rounds of feedback"
      brief={BRIEF}
      fullBleedContent
    >
      <Helmet>
        <title>Prototype: Quotes page</title>
      </Helmet>
      <div className="flex min-h-screen shrink-0 flex-col">
        <ProductFlowNav
          matchedBroker={MATCHED_BROKER}
          cartCount={cart.count}
          onCartClick={cart.open}
          disableScrollHide
        />
        <div className="flex flex-1 flex-col">
          <div className="mx-auto w-full max-w-(--maxw) px-5.5 py-4">
            <ApplicationSummarySection data={SUMMARY_DATA} />
            <FiltersSection
              sortBy={sortBy}
              onSortByChange={setSortBy}
              selectedRatings={selectedRatings}
              onToggleRating={toggleRating}
              onClearRatings={() => setSelectedRatings(new Set())}
              selectedCoiSpeeds={selectedCoiSpeeds}
              onToggleCoiSpeed={toggleCoiSpeed}
              onClearCoiSpeeds={() => setSelectedCoiSpeeds(new Set())}
              selectedCoverages={selectedCoverages}
              onToggleCoverage={toggleCoverage}
              onClearCoverages={() => setSelectedCoverages(new Set())}
              financingAvailable={financingAvailable}
              onToggleFinancingAvailable={() => setFinancingAvailable((current) => !current)}
              onClearFinancingAvailable={() => setFinancingAvailable(false)}
              selectedClaimsCapabilities={selectedClaimsCapabilities}
              onToggleClaimsCapability={toggleClaimsCapability}
              onClearClaimsCapabilities={() => setSelectedClaimsCapabilities(new Set())}
              onClearAllFilters={clearAllFilters}
              isUpdatingQuotes={isUpdatingQuotes}
            />
            <TopQuotesHeader
              onScrollPrev={() => carouselRef.current?.scrollPrev()}
              onScrollNext={() => carouselRef.current?.scrollNext()}
              policyExpirationDate={POLICY_EXPIRATION_DATE}
            />
            {missingCoverages.length > 0 && (
              <div className="mx-auto mb-6 flex w-full max-w-sm flex-col gap-3">
                {missingCoverages.map((coverage) => (
                  <Banner
                    key={coverage.value}
                    small
                    action={
                      <Button type="button" variant="outline" size="sm">
                        Request
                      </Button>
                    }
                  >
                    Want quotes for {coverage.label} Coverage?
                  </Banner>
                ))}
              </div>
            )}
            <QuoteCardsCarousel
              ref={carouselRef}
              quotes={visibleQuotes}
              orderKey={orderKey}
              onAddCoverages={cart.add}
              onRemoveCoverages={cart.remove}
              cartKeys={cart.keys}
              revealFocusedCard
              // Keeps the NEEDS_ATTESTATION Review button on show; there's no org here to accept for.
              onReviewAttestation={() => undefined}
            />
            <QuoteCartCheckout quotes={QUOTES} onSubmit={submitCart} />
            <PlaceholderSection title="Financing">
              <p className="text-ink-400 text-sm">
                Cai can help your customers get financing through First Insurance Funding.
              </p>
            </PlaceholderSection>
            <PlaceholderSection title="Other Underwriting Data">
              <p className="text-ink-400 text-sm">
                FMCSA data, CABS data, and an upsell to the Cai Compliance product for year-round ways to
                lower your insurance rates.
              </p>
            </PlaceholderSection>
          </div>
        </div>
        <ProductFlowFooter
          legalLinks={[
            { label: 'Privacy Policy', href: '/privacy' },
            { label: 'Terms of Service', href: '/terms' },
          ]}
        />
      </div>
    </PrototypeFrame>
  )
}
